import type { MetaStructureResponse } from '@nono/contract';
import { flatAds, type Flat, type Period, resultKind } from './model';

/*
 * The ad production system: every ad is ONE combination of an angle (why she
 * clicks) × a sender (who says it) × a hook × a destination (form or booking).
 *
 * New ads carry their combination in the name — the «DNA»:
 *   V:ledd | A:ronnaug | F:video | H:02 | D:book | v1
 * so the board sorts them exactly. The 97 ads made before this have free-text
 * names; they are sorted by keyword as well as the names allow, and the rest is
 * shown honestly as «Uklassifisert».
 */

export type AngleId = 'hater' | 'ledd' | 'vekt' | 'tid' | 'livet' | 'folk' | 'tilbud';
export type SenderId = 'ronnaug' | 'amund' | 'ai' | 'medlem' | 'bilde' | 'motion';
export type Dest = 'book' | 'lead';

export interface Angle {
  id: AngleId;
  name: string;
  who: string;
  color: string;
  hooks: string[];
  /** What we must not promise in this angle (legal scrub 23.06, health claims). */
  guard?: string;
  match: RegExp;
}

export const ANGLES: Angle[] = [
  {
    id: 'hater',
    name: 'Hater treningssenteret',
    who: 'Har aldri likt gymmen: speil, kroppspress, støy.',
    color: '#7b97c4',
    hooks: [
      'Du hater ikke trening. Du hater treningssenteret.',
      'Ingen speil. Ingen kø. Ingen som ser på deg.',
      'Ikke enda et treningssenter.',
    ],
    match: /(treningssent|gympress|ikke enda|ikke et vanlig|vanlig|stillhet|trygt sted|ny treningsform|comparison|sliten)/,
  },
  {
    id: 'ledd',
    name: 'Vonde ledd og helse',
    who: '50–69 med rygg, kne, artrose eller etter operasjon.',
    color: '#c58b5f',
    hooks: [
      'Kneet ditt trenger ikke hvile. Det trenger styrke.',
      'Vond rygg? Slik trener du uten å gjøre det verre.',
      'Fysioterapeuten sa «styrketrening». Her starter du.',
    ],
    guard: 'Ingen løfter om å bli smertefri eller frisk. Si «trygt for vonde ledd», ikke «kurerer».',
    match: /(ledd|rygg|kne|vond|artrose|stiv|smerte|kroppen fortjener|musklene)/,
  },
  {
    id: 'vekt',
    name: 'Gå ned i vekt',
    who: 'Vil ned i vekt og har prøvd å løpe det av seg.',
    color: '#d07a8c',
    hooks: [
      'Du mister ikke fett av å trene. Men du bygger det som brenner.',
      'Trening gjør deg sultnere. Derfor trener vi annerledes.',
      'Slutt å løpe. Begynn å bli sterk.',
    ],
    guard: 'Ingen kilo, ingen «brenner mer fett»-tall. Lydsporet i filmene fra 10.10 har uverifiserte påstander.',
    match: /(vekt|fett|sultne|kilo|slank)/,
  },
  {
    id: 'tid',
    name: 'Hele kroppen på under 40 min',
    who: 'Travel, vil ha det gjort: én runde, ferdig.',
    color: '#5fae9b',
    hooks: [
      'Hele kroppen. Åtte maskiner. Under 40 minutter.',
      'Ingen plan å følge. Bare én runde, og så er du ferdig.',
      'To ganger i uka er nok.',
    ],
    guard: 'Økta er «under 40 minutter» (8 maskiner × 3 min + bytter). Ikke lov 30 min uten å sjekke.',
    match: /(30 ?min|40 ?min|effektiv|hele kroppen|to dager|enkel|punktlist|du slipper)/,
  },
  {
    id: 'livet',
    name: 'Sterk for resten av livet',
    who: '70+ som vil klare seg selv: trapper, barnebarn, poser.',
    color: '#8fae8b',
    hooks: [
      'Hun begynte å trene da hun var 79.',
      'Det handler ikke om å bli sprek. Det handler om å klare seg selv.',
      'Det er aldri for sent å bli sterkere.',
    ],
    match: /(87|90 år|\b70|eldre|livet|ikke kun for unge|burde trene|alder|barnebarn)/,
  },
  {
    id: 'folk',
    name: 'Folk som deg',
    who: 'Trenger å se at «noen som meg» er der og trives.',
    color: '#b08cc7',
    hooks: [
      '«Jeg gruet meg i tre uker. Nå gleder jeg meg.»',
      'Rønnaug (57) og Amund (27): samme system, hvert sitt nivå.',
    ],
    match: /(spiller på lag|\blag\b|åpnet|medlem|historie|eint av|hvorfor de|testimonial|kom innom|drop in|har du lyst|nivå 10|walkthrough|dame|mann)/,
  },
  {
    id: 'tilbud',
    name: 'Tilbudet (649 kr)',
    who: 'Varme folk som trenger en grunn til å gjøre det nå.',
    color: '#d1a54a',
    hooks: ['Gratis prøvetime. Så 649 kr i måneden, hvis du vil fortsette.'],
    guard: 'Før prøvetimen: bare «649 kr/mnd · 12 mnd binding», aldri 899.',
    match: /(649|pris|billig|garanti|gratis|kampanje|bilde [a-e]\b)/,
  },
];

export interface Sender {
  id: SenderId;
  name: string;
  how: string;
  match: RegExp;
}

export const SENDERS: Sender[] = [
  {
    id: 'ronnaug',
    name: 'Rønnaug (57)',
    how: 'Snakker rett i kamera, 20–40 s. Ekte, rolig, «en som meg».',
    match: /r[øo]n+[au]+g/,
  },
  {
    id: 'amund',
    name: 'Amund (ung)',
    how: 'Vert som viser og forklarer. Walkthrough, nivå, maskinene.',
    match: /amund/,
  },
  {
    id: 'ai',
    name: 'AI-stemme + footage',
    how: 'ElevenLabs-stemme over ekte footage fra salen. Rask å lage, lett å teste mange vinkler.',
    match: /(\bai\b|ai -|ai angles|ai ads|voice|elevenlabs)/,
  },
  {
    id: 'medlem',
    name: 'Medlemshistorie',
    how: 'Et ekte medlem forteller med egne ord. Ikke klipp bort historien.',
    match: /(medlem|historie|testimonial|hvorfor de|dame|mann)/,
  },
  {
    id: 'bilde',
    name: 'Stillbilde',
    how: 'Ett bilde, én setning. Billig å lage, bra til tilbud og varme folk.',
    match: /(photo|bilde|still|image)/,
  },
  {
    id: 'motion',
    name: 'Motion graphics',
    how: 'Rolig forklaringsfilm: én idé per film, ekte footage + Brand Reset.',
    match: /(motion|grafikk|animasjon|explainer)/,
  },
];

export const ANGLE_BY_ID = new Map(ANGLES.map((a) => [a.id, a]));
export const SENDER_BY_ID = new Map(SENDERS.map((s) => [s.id, s]));

/** Round 1 of the test plan: one sender across every angle (fast to make, same voice). */
export const ROUND1_SENDER: SenderId = 'ai';

const ANGLE_ORDER: AngleId[] = ['ledd', 'livet', 'vekt', 'tid', 'hater', 'folk', 'tilbud'];
const SENDER_ORDER: SenderId[] = ['ronnaug', 'amund', 'motion', 'ai', 'bilde', 'medlem'];

function dna(name: string, key: string): string | null {
  const m = name.match(new RegExp(`(?:^|\\|)\\s*${key}:\\s*([a-zæøå0-9-]+)`, 'i'));
  return m?.[1]?.toLowerCase() ?? null;
}

export function angleOf(adName: string): AngleId | null {
  const v = dna(adName, 'V');
  if (v && ANGLE_BY_ID.has(v as AngleId)) return v as AngleId;
  const n = adName.toLowerCase();
  for (const id of ANGLE_ORDER) if (ANGLE_BY_ID.get(id)!.match.test(n)) return id;
  return null;
}

/** The ad's own name first; the ad set name only when the ad name says nothing. */
export function senderOf(adName: string, adsetName: string): SenderId | null {
  const a = dna(adName, 'A');
  if (a && SENDER_BY_ID.has(a as SenderId)) return a as SenderId;
  for (const text of [adName.toLowerCase(), adsetName.toLowerCase()])
    for (const id of SENDER_ORDER) if (SENDER_BY_ID.get(id)!.match.test(text)) return id;
  return null;
}

export interface CellStats {
  ads: Flat[];
  spend: number; // NOK
  book: { spend: number; n: number }; // booking-destination ads
  lead: { spend: number; n: number }; // form ads
  impressions: number;
  video3s: number;
  thruplays: number;
}

export type CellStatus = 'untested' | 'young' | 'winner' | 'ok' | 'loser';

export interface Matrix {
  cells: Map<string, CellStats>; // key `${angle}|${sender}`; null parts → '?'
  unclassified: Flat[];
  totals: { classified: number; all: number };
}

export const cellKey = (a: AngleId | null, s: SenderId | null) => `${a ?? '?'}|${s ?? '?'}`;

export function buildMatrix(d: MetaStructureResponse, period: Period): Matrix {
  const fx = d.fxAedToNok;
  const cells = new Map<string, CellStats>();
  const unclassified: Flat[] = [];
  const all = flatAds(d);
  for (const f of all) {
    const a = angleOf(f.ad.name);
    const s = senderOf(f.ad.name, f.adset.name);
    if (!a && !s) {
      unclassified.push(f);
      continue;
    }
    const k = cellKey(a, s);
    const c = cells.get(k) ?? {
      ads: [],
      spend: 0,
      book: { spend: 0, n: 0 },
      lead: { spend: 0, n: 0 },
      impressions: 0,
      video3s: 0,
      thruplays: 0,
    };
    const p = f.ad[period];
    const spend = p.spend * fx;
    c.ads.push(f);
    c.spend += spend;
    c.impressions += p.impressions;
    c.video3s += p.video3s;
    c.thruplays += p.thruplays;
    if (resultKind(f.adset) === 'booking') {
      c.book.spend += spend;
      c.book.n += p.schedules;
    } else {
      c.lead.spend += spend;
      c.lead.n += p.leads;
    }
    cells.set(k, c);
  }
  return { cells, unclassified, totals: { classified: all.length - unclassified.length, all: all.length } };
}

/*
 * A cell's verdict, with the same bands as the account map (model.ts): a
 * booking under 400 kr / a lead under 110 kr is a winner, over 700 / 200 a
 * loser. Under 300 kr spent is too early to say anything.
 */
export function cellStatus(c: CellStats | undefined): CellStatus {
  if (!c || !c.ads.length) return 'untested';
  if (c.spend < 300) return 'young';
  const cpb = c.book.n ? c.book.spend / c.book.n : c.book.spend >= 700 ? Infinity : null;
  const cpl = c.lead.n ? c.lead.spend / c.lead.n : c.lead.spend >= 300 ? Infinity : null;
  const good = (cpb !== null && cpb <= 400) || (cpl !== null && cpl <= 110);
  const bad = (cpb === null || cpb >= 700) && (cpl === null || cpl >= 200);
  return good ? 'winner' : bad ? 'loser' : 'ok';
}

/* ── Naming ──────────────────────────────────────────────────────────────── */

export const FORMATS = ['video', 'bilde', 'karusell'] as const;
export type Format = (typeof FORMATS)[number];

export function dnaName(o: {
  angle: AngleId;
  sender: SenderId;
  format: Format;
  hook: number;
  dest: Dest;
  version: number;
}): string {
  return `V:${o.angle} | A:${o.sender} | F:${o.format} | H:${String(o.hook).padStart(2, '0')} | D:${o.dest} | v${o.version}`;
}

/* ── Rhythm and rules ────────────────────────────────────────────────────── */

export const ROUNDS = [
  {
    n: 1,
    title: 'Vinkel',
    what: 'Én avsender (AI-stemme + footage) gjennom alle vinklene. Svarer på: hva får folk til å booke?',
    weeks: 'Uke 1–3',
  },
  {
    n: 2,
    title: 'Avsender',
    what: 'De to beste vinklene, med Rønnaug, Amund, medlemshistorie og motion graphics. Samme budskap.',
    weeks: 'Uke 3–5',
  },
  {
    n: 3,
    title: 'Hook',
    what: 'Vinnerkombinasjonen med 3–5 nye åpningslinjer. Den beste blir motoren.',
    weeks: 'Uke 5+',
  },
] as const;

export const RHYTHM = [
  { day: 'Mandag', what: 'Les tavla. Drep taperne, flytt vinnerne inn i motoren.' },
  { day: 'Tirsdag', what: 'Lag 3–5 nye annonser til de neste rutene i matrisen.' },
  { day: 'Onsdag', what: 'Nye annonser inn i testbanen (fast budsjett, ~150 kr/dag).' },
] as const;

export const RULES = [
  { tone: 'bad' as const, rule: 'Drep', when: 'Brukt ~1 000 kr uten booking, eller hook rate under 20 % etter 2 000 visninger.' },
  { tone: 'good' as const, rule: 'Skaler', when: 'Pris per booking under 450 kr over minst 3 bookinger.' },
  { tone: 'neutral' as const, rule: 'Vent', when: 'Under 300 kr brukt. For tidlig å si noe.' },
];

/** Daily goal and what it costs at the account's own booking price. */
export const GOAL_PER_DAY = 2;
export const BUDGET_PER_DAY = 1000; // owner yes, 2026-10-19
